import React from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/SubsidiaryModal";
import * as AlfredoComponents from "../..";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface SubsidiaryModalProps {
  selectedObject: any;
  selectedInfo: any;
  handleCloseModal: () => void;
  handleSave: (objectType: any) => void;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  setSelectedInfo: (info: any) => void;
  setTempFormData: (data: any) => void;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
}

const SubsidiaryModal: React.FC<SubsidiaryModalProps> = ({
  selectedObject,
  selectedInfo,
  handleCloseModal,
  handleSave,
  handleInputChange,
  setSelectedInfo,
  setTempFormData,
  parseInfoValue,
  decryptedInfoToken,
  handleOpenModal,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-subsidiarymodal",
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatUser = (userId: string) => {
    const USER_MAP: { [key: string]: string } = {
      "e16904f8-1d9b-4579-907c-2cf5dcc9979f": "Administrador",
      default: "Usuário do Sistema",
    };
    return USER_MAP[userId] || USER_MAP.default;
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} large>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-subsidiarymodal">
            <AlfredoStyles.MaterialIcons>store</AlfredoStyles.MaterialIcons>
            Loja - {selectedObject?.subsidiaryName || "Nova Loja"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.SubsidiaryContainer>
            {/* Informações Principais */}
            <AlfredoStyles.SubsidiaryTopContainer>
              <AlfredoStyles.FormSection>
                <AlfredoStyles.SectionTitle>
                  Informações da Loja
                </AlfredoStyles.SectionTitle>
                <AlfredoStyles.FormRow>
                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        storefront
                      </AlfredoStyles.MaterialIcons>
                      Nome da Loja:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.subsidiaryName || ""}
                      onChange={(e) => handleInputChange(e, "subsidiaryName")}
                      placeholder="Digite o nome da loja"
                    />
                  </AlfredoStyles.LabelInputContainer>

                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        description
                      </AlfredoStyles.MaterialIcons>
                      Descrição:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.subsidiaryDescription || ""}
                      onChange={(e) =>
                        handleInputChange(e, "subsidiaryDescription")
                      }
                      placeholder="Descrição da loja"
                    />
                  </AlfredoStyles.LabelInputContainer>
                </AlfredoStyles.FormRow>

                <AlfredoStyles.FormRow>
                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        fingerprint
                      </AlfredoStyles.MaterialIcons>
                      CNPJ:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.subsidiaryTaxPayerID || ""}
                      onChange={(e) =>
                        handleInputChange(e, "subsidiaryTaxPayerID")
                      }
                      placeholder="00.000.000/0000-00"
                    />
                  </AlfredoStyles.LabelInputContainer>

                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        phone
                      </AlfredoStyles.MaterialIcons>
                      Telefone:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.subsidiaryPhone || ""}
                      onChange={(e) => handleInputChange(e, "subsidiaryPhone")}
                      placeholder="(00) 00000-0000"
                    />
                  </AlfredoStyles.LabelInputContainer>
                </AlfredoStyles.FormRow>
              </AlfredoStyles.FormSection>

              {/* Metadados */}
              <AlfredoStyles.FormSection>
                <AlfredoStyles.SectionTitle>
                  Informações do Sistema
                </AlfredoStyles.SectionTitle>
                <AlfredoStyles.InfoGrid>
                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        date_range
                      </AlfredoStyles.MaterialIcons>
                      Criado em:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue>
                      {formatDate(selectedObject.createdAt)}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>

                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        update
                      </AlfredoStyles.MaterialIcons>
                      Última atualização:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue>
                      {formatDate(selectedObject.updatedAt)}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>

                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        person
                      </AlfredoStyles.MaterialIcons>
                      Alterado por:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue>
                      {formatUser(selectedObject.updatedBy)}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>

                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        security
                      </AlfredoStyles.MaterialIcons>
                      Status:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue
                      status={selectedObject.subsidiaryStatus}
                    >
                      {selectedObject.subsidiaryStatus ? "Ativa" : "Inativa"}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>
                </AlfredoStyles.InfoGrid>
              </AlfredoStyles.FormSection>
            </AlfredoStyles.SubsidiaryTopContainer>

            {/* Navegação e Conteúdo Dinâmico */}
            <AlfredoStyles.SubsidiaryBottom>
              <AlfredoStyles.SubsidiaryInfoContainer>
                <AlfredoStyles.SubsidiaryInfoList>
                  <AlfredoStyles.SubsidiaryInfo
                    selected={!selectedInfo?.subsidiaryInfoType}
                    onClick={() => {
                      setSelectedInfo({ subsidiaryInfoType: 0 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>info</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Informações Gerais</div>
                      <small>Dados básicos da loja</small>
                    </div>
                  </AlfredoStyles.SubsidiaryInfo>

                  <AlfredoStyles.SubsidiaryInfo
                    selected={selectedInfo?.subsidiaryInfoType === 1}
                    onClick={() => {
                      setSelectedInfo({ subsidiaryInfoType: 1 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>
                      category
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Grupos de Produtos</div>
                      <small>Gerencie grupos</small>
                    </div>
                  </AlfredoStyles.SubsidiaryInfo>

                  <AlfredoStyles.SubsidiaryInfo
                    selected={selectedInfo?.subsidiaryInfoType === 2}
                    onClick={() => {
                      setSelectedInfo({ subsidiaryInfoType: 2 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>notes</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Observações</div>
                      <small>Anotações internas</small>
                    </div>
                  </AlfredoStyles.SubsidiaryInfo>

                  <AlfredoStyles.SubsidiaryInfo
                    selected={selectedInfo?.subsidiaryInfoType === 3}
                    onClick={() => {
                      setSelectedInfo({ subsidiaryInfoType: 3 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>receipt</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Taxas</div>
                      <small>Taxas aplicáveis</small>
                    </div>
                  </AlfredoStyles.SubsidiaryInfo>

                  <AlfredoStyles.SubsidiaryInfo
                    selected={selectedInfo?.subsidiaryInfoType === 4}
                    onClick={() => {
                      setSelectedInfo({ subsidiaryInfoType: 4 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>
                      local_offer
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Descontos</div>
                      <small>Políticas de desconto</small>
                    </div>
                  </AlfredoStyles.SubsidiaryInfo>

                  <AlfredoStyles.SubsidiaryInfo
                    selected={selectedInfo?.subsidiaryInfoType === 5}
                    onClick={() => {
                      setSelectedInfo({ subsidiaryInfoType: 5 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>
                      payments
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Formas de Pagamento</div>
                      <small>Métodos aceitos</small>
                    </div>
                  </AlfredoStyles.SubsidiaryInfo>
                </AlfredoStyles.SubsidiaryInfoList>

                <AlfredoStyles.SubsidiaryInfoContent>
                  {(!selectedInfo?.subsidiaryInfoType ||
                    selectedInfo.subsidiaryInfoType === 0) && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Informações Gerais da Loja
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.FormGrid>
                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Email:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="email"
                            value={selectedObject.subsidiaryEmail || ""}
                            onChange={(e) =>
                              handleInputChange(e, "subsidiaryEmail")
                            }
                            placeholder="loja@exemplo.com"
                          />
                        </AlfredoStyles.LabelInputContainer>

                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Endereço:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="text"
                            value={selectedObject.subsidiaryAddress || ""}
                            onChange={(e) =>
                              handleInputChange(e, "subsidiaryAddress")
                            }
                            placeholder="Endereço completo"
                          />
                        </AlfredoStyles.LabelInputContainer>

                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Cidade:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="text"
                            value={selectedObject.subsidiaryCity || ""}
                            onChange={(e) =>
                              handleInputChange(e, "subsidiaryCity")
                            }
                            placeholder="Cidade"
                          />
                        </AlfredoStyles.LabelInputContainer>

                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Estado:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="text"
                            value={selectedObject.subsidiaryState || ""}
                            onChange={(e) =>
                              handleInputChange(e, "subsidiaryState")
                            }
                            placeholder="UF"
                          />
                        </AlfredoStyles.LabelInputContainer>
                      </AlfredoStyles.FormGrid>
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.subsidiaryInfoType === 1 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Grupos de Produtos
                      </AlfredoStyles.SectionTitle>
                      <AlfredoComponents.DynamicTable
                        tableType={3}
                        handleOpenModal={handleOpenModal}
                      />
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.subsidiaryInfoType === 2 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Observações Internas
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.TextArea
                        as="textarea"
                        value={selectedObject.subsidiaryNotes || ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          handleInputChange(e, "subsidiaryNotes")
                        }
                        placeholder="Anotações importantes sobre a loja..."
                        rows={6}
                      />
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.subsidiaryInfoType === 3 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Taxas Aplicáveis
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.AlertMessage type="info">
                        <AlfredoStyles.MaterialIcons>
                          info
                        </AlfredoStyles.MaterialIcons>
                        Configure as taxas aplicáveis a esta loja.
                      </AlfredoStyles.AlertMessage>
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.subsidiaryInfoType === 4 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Políticas de Desconto
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.AlertMessage type="info">
                        <AlfredoStyles.MaterialIcons>
                          info
                        </AlfredoStyles.MaterialIcons>
                        Configure as políticas de desconto para esta loja.
                      </AlfredoStyles.AlertMessage>
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.subsidiaryInfoType === 5 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Formas de Pagamento
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.AlertMessage type="info">
                        <AlfredoStyles.MaterialIcons>
                          info
                        </AlfredoStyles.MaterialIcons>
                        Configure os métodos de pagamento aceitos por esta loja.
                      </AlfredoStyles.AlertMessage>
                    </AlfredoStyles.InfoSection>
                  )}
                </AlfredoStyles.SubsidiaryInfoContent>
              </AlfredoStyles.SubsidiaryInfoContainer>
            </AlfredoStyles.SubsidiaryBottom>
          </AlfredoStyles.SubsidiaryContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={() => handleSave("subsidiary")}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              Salvar Alterações
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default SubsidiaryModal;
